'use client';

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useDeviceType } from '@/hooks/useIsMobile';
import { DeviceType } from '@/types/services';
import ImageAdaptive from '@/components/ImageAdaptive';

interface PopoverProps {
    label: string;
    sections: {
        label: string;
        jp: string;
        Icon: any;
        items: { label: string; jp: string; path: string; disableOn?: DeviceType[] }[];
    }[];
    position: { top: number; left: number };
    direction?: 'up' | 'down';
    isCentered?: boolean;
    onNavigate: (path: string) => void;
    onClose: () => void;
}

export default function Popover({
    label,
    sections,
    position,
    direction = 'down',
    isCentered = false,
    onNavigate,
    onClose,
}: PopoverProps) {
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const style = {
        top: direction === 'up' ? `${position.top - 60}px` : `${position.top + 5}px`,
        left: isCentered ? `50%` : `${position.left + 20}px`,
        transform: direction === 'up' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
        position: 'fixed' as const,
    };

    const { isMobileDevice, isTabletDevice, isDesktopDevice } = useDeviceType();

    const checkIfDisabled = (disabledOn?: DeviceType[]) => {
        if (!disabledOn) return false;
        if (isMobileDevice && disabledOn.includes('mobile')) return true;
        if (isTabletDevice && disabledOn.includes('tablet')) return true;
        if (isDesktopDevice && disabledOn.includes('desktop')) return true;
        return false;
    };

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
            ref={popoverRef}
            className="rounded-lg shadow-right-bottom w-[230px] md:w-[230px] p-3 z-[9000] bg-color-base"
            style={style}
        >
            <div className="flex flex-col space-y-3">
                {sections.map((section) => (
                    <div key={section.label}>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="flex mt-1">
                                <div className="font-montserrat font-black text-sm text-color-white-base">
                                    {section.label}
                                </div>
                                <div className="font-corp font-bold text-xs text-color-white-base ml-[4px] mt-[2.5px]">
                                    {section.jp}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col py-2">
                            {section.items.map((item) => {
                                const isDisabled = checkIfDisabled(item.disableOn);
                                return (
                                    <div
                                        key={item.label + item.path}
                                        className={`flex items-center py-1 px-3 rounded transition-colors duration-150 ${
                                            isDisabled
                                                ? 'opacity-40 cursor-not-allowed'
                                                : 'cursor-pointer group [@media(hover:hover)]:hover:bg-color-white-base/20'
                                        }`}
                                        onClick={() => {
                                            if (isDisabled) return;
                                            if (item.path === '') return;
                                            onNavigate(item.path);
                                            onClose();
                                        }}
                                    >
                                        <div className="flex pr-3 opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-150">
                                            <ImageAdaptive src="/images/common/caret-right.svg" baseWidth={8} />
                                        </div>
                                        <div className="flex font-corp font-medium text-sm text-color-white-base">
                                            {item.jp}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>,
        document.body,
    );
}
